import { differenceInDays } from 'date-fns';
import { useMediaQuery } from 'react-responsive';

import { useLocale, useTranslations } from 'next-intl';

import { RatingScore } from '@/components/reviews/RatingScore';
import { Badge } from '@/component/Badge';
import {
  buildListingDetailsQuery,
  getListingLocaleSlug,
  hasFilters,
} from '@/utils';
import { pushListingCard, mapGtmItem } from '@/utils/gtm';
import { useSearchState } from './hooks';

import styles from './Card.module.css';
import { CardPrice } from './CardPrice';
import { useParams } from 'next/navigation';
import { Link, useRouter } from '@/i18n/routing';
import { CardCarousel } from './CardCarousel';
import { ReviewsStars } from '@/component/Reviews/ReviewsStars';

export const ListingsCard = ({
  location,
  guests,
  tab,
  rooms,
  rating,
  price,
  amenities,
  list,
  date,
  style,
  registerChild,
  dataTestId,
  pageName = 'results page',
}) => {
  const { push } = useRouter();
  const locale = useLocale();
  const query = useParams();
  const t = useTranslations();
  const { searchState } = useSearchState(query);

  const daysAmount =
    !!date.startDate && !!date.endDate
      ? differenceInDays(new Date(date.endDate), new Date(date.startDate)) || 1
      : 1;

  const visibleTitle =
    list?.name?.length > 55 ? `${list?.name.slice(0, 55)}...` : list?.name;

  const isMobile = useMediaQuery({ query: '(max-width: 639px)' });
  const isHasFilters = hasFilters(searchState);

  const pushGTM = () =>
    pushListingCard(
      isHasFilters ? `filtered ${pageName}` : pageName,
      mapGtmItem(list)
    );

  const cardUrl = {
    href: `/listings/${getListingLocaleSlug(
      list,
      locale
    )}${buildListingDetailsQuery(
      list?.price?.startDate || date.startDate,
      list?.price?.endDate || date.endDate,
      guests,
      location,
      tab,
      rooms,
      rating,
      price,
      amenities
    )}`,
    target: isMobile ? '_self' : '_blank',
    onClick: pushGTM,
  };

  return (
    <div
      className={styles.inner}
      style={style}
      ref={registerChild}
      data-testid={dataTestId}
    >
      <div className={styles.card}>
        <CardCarousel previewImages={list?.previewImages} cardUrl={cardUrl} />
        <Link {...cardUrl} className={styles.card_body} prefetch={false}>
          <div className="flex items-center gap-[10px] max-lg:mb-2.5 lg:mb-3">
            {list?.numReviews > 0 && (
              <div className="flex items-center text-slate-500 max-sm:text-xs text-sm gap-1">
                <ReviewsStars stars={list?.ratingScore} />
                <span data-testid="card-rating-score"><RatingScore rating={list?.ratingScore} /></span>
                <span data-testid="card-num-reviews">({list?.numReviews})</span>
              </div>
            )}
            {list?.label && (
              <span
                className={`${styles.card_label} ${styles[list?.label?.color]}`}
              >
                {list?.label?.name}
              </span>
            )}
          </div>

          <div className={styles.card_body_info}>
            <h2 title={visibleTitle}>{visibleTitle}</h2>
            <ul>
              {!!list?.occupancy?.numGuests && (
                <li data-testid="card-guests">
                  {`${list?.occupancy?.numGuests} ${
                    list?.occupancy?.numGuests === 1 ? t('guest') : t('guests')
                  }`}
                </li>
              )}
              {!!list?.occupancy?.numBedrooms && (
                <li data-testid="card-bedrooms">
                  {`${list?.occupancy?.numBedrooms} ${
                    list?.occupancy?.numBedrooms === 1
                      ? t('bedroom')
                      : t('bedrooms')
                  }`}
                </li>
              )}
              {!!list?.occupancy?.numBeds && (
                <li data-testid="card-beds">
                  {`${list?.occupancy?.numBeds} ${
                    list?.occupancy?.numBeds === 1 ? t('bed') : t('beds')
                  }`}
                </li>
              )}
              {list?.occupancy?.petsAllowed ? (
                <li data-testid="card-pets">{t('pets-allowed')}</li>
              ) : null}
            </ul>
          </div>

          <div className={styles.card_footer}>
            {!!list?.price && (
              <CardPrice
                daysAmount={daysAmount}
                date={date}
                price={list?.price}
              />
            )}
          </div>

          {!!list?.price?.discount?.percentage && (
            <Badge
              value={list?.price?.discount?.percentage}
              type={list?.price?.discount?.type}
            />
          )}
        </Link>
      </div>
    </div>
  );
};
